import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft,
  FileText,
  Upload,
  Download,
  Trash2,
  Eye,
  Clock,
  Users,
  AlertTriangle,
  CheckCircle,
  History,
  Settings,
  Plus,
  ExternalLink,
  GitBranch,
  AlertCircle
} from 'lucide-react';
import MaterialUpload from './MaterialUpload';
import VersionControl from './VersionControl';
import ImpactPreview from './ImpactPreview';
import { 
  fetchSessionById
} from '@/lib/airtable';

/**
 * SessionMaterials - Session-specific materials management interface
 * 
 * Comprehensive interface for managing materials within a specific session.
 * Includes file upload, version control, and impact preview functionality.
 * 
 * Features:
 * - Current materials list with CDN URLs
 * - Drag-and-drop file upload
 * - Version history and rollback
 * - Impact preview showing affected SOs
 * - Individual file management
 */
const SessionMaterials = () => {
  const { programId, sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('current');
  const [impactData, setImpactData] = useState(null);

  useEffect(() => {
    const loadSessionData = async () => {
      try {
        setLoading(true);
        
        // TODO: Replace with real materials management once database tables are set up
        // For now, just load basic session data and use placeholders for materials
        
        // Fetch basic session details only
        const sessionData = await fetchSessionById(sessionId);
        
        // Log for debugging
        console.log('Session basic data loaded:', {
          sessionId,
          hasSessionData: !!sessionData
        });
        
        // Transform session data to match expected format
        // Handle array fields from Airtable lookups
        const soNumber = Array.isArray(sessionData.soNumber) 
          ? sessionData.soNumber[0] 
          : sessionData.soNumber;
        
        const programName = Array.isArray(sessionData.programName) 
          ? sessionData.programName[0] 
          : sessionData.programName || 'Unknown Program';
        
        const sessionInfo = {
          id: sessionData.id,
          programId: programId,
          name: sessionData.sessionName || `Session ${sessionData.sessionNo}`,
          sessionNumber: sessionData.sessionNo || 0,
          programName: programName,
          programCode: sessionData.programCode || '',
          duration: sessionData.duration || 120,
          currentVersion: 'v1.0.0', // Placeholder version
          lastUpdated: new Date().toISOString(),
          activeSOs: 0, // Placeholder - would require real calculation
          description: sessionData.description || sessionData.sessionName || `Session ${sessionData.sessionNo}`,
          soNumber: soNumber
        };

        // Use placeholder materials data
        const transformedMaterials = [
          {
            id: 'placeholder-1',
            filename: 'Sample_Materials.pdf',
            displayName: 'Sample Materials (Placeholder)',
            fileSize: '2.4 MB',
            fileSizeBytes: 2400000,
            contentType: 'application/pdf',
            uploadedAt: new Date().toISOString(),
            uploadedBy: 'System',
            version: 'v1.0.0',
            cdnUrl: '#',
            shortCode: 'placeholder',
            shortLink: 'Coming Soon',
            downloadCount: 0,
            status: 'placeholder'
          }
        ];

        // Use placeholder versions data
        const transformedVersions = [
          {
            id: 'version-placeholder-1',
            versionId: 'v1.0.0',
            createdAt: new Date().toISOString(),
            createdBy: 'System',
            changeNotes: 'Initial placeholder version - materials management system in development',
            filesCount: 1,
            totalSize: '2.4 MB',
            status: 'placeholder',
            isCurrent: true,
            sosUsingVersion: 0
          }
        ];

        // Create placeholder impact data
        const placeholderImpactData = {
          currentVersion: 'v1.0.0',
          affectedSOs: [], // TODO: Calculate from real data
          totalImpact: 0,
          upcomingSessions: 0, // TODO: Calculate from real data
          completedSessions: 0 // TODO: Calculate from real data
        };

        setSession(sessionInfo);
        setMaterials(transformedMaterials);
        setVersions(transformedVersions);
        setImpactData(placeholderImpactData);
        
      } catch (err) {
        console.error('Failed to load session data:', err);
        
        // Handle specific error cases
        if (err.message?.includes('NOT_FOUND')) {
          setError('Session not found');
        } else if (err.message?.includes('permission')) {
          setError('Access denied - insufficient permissions');
        } else {
          setError(err.message || 'Failed to load session information');
        }
      } finally {
        setLoading(false);
      }
    };

    loadSessionData();
  }, [programId, sessionId]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const handleFileUpload = (files) => {
    // TODO: Implement actual file upload to Cloudflare R2
  };

  const handleVersionRollback = (versionId) => {
    // TODO: Implement version rollback functionality
  };

  const handleDeleteFile = (fileId) => {
    // TODO: Implement file deletion
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-4">
          <Skeleton className="h-8 w-8" />
          <div>
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-96" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="p-4 border rounded-lg">
                  <Skeleton className="h-5 w-48 mb-2" />
                  <Skeleton className="h-4 w-32 mb-2" />
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="space-y-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate(`/admin/materials/programs/${programId}`)}
          className="flex items-center space-x-2"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Program</span>
        </Button>
        
        <Card>
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-600 font-medium">Failed to load session</p>
            <p className="text-gray-500 text-sm mt-1">{error || 'Session not found'}</p>
            <Button 
              onClick={() => window.location.reload()} 
              variant="outline" 
              className="mt-4"
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Materials Management Status Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-amber-800">Session Materials Management In Development</h3>
            <p className="text-sm text-amber-700 mt-1">
              This interface shows session information and placeholder materials data. Full materials management functionality 
              will be available once the materials database tables are configured. Upload, versioning, and file management 
              features are temporarily disabled.
            </p>
          </div>
        </div>
      </div>

      {/* Three-Tier Breadcrumb Navigation */}
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <Link 
          to="/admin/materials" 
          className="hover:text-gray-700 flex items-center space-x-1"
        >
          <span>Materials</span>
        </Link>
        <span>→</span>
        <Link 
          to={`/admin/materials/programs/${programId}`}
          className="hover:text-gray-700"
        >
          <span>{session.programName}</span>
        </Link>
        <span>→</span>
        <span className="font-medium text-gray-900">
          Session {session.sessionNumber}: {session.name}
        </span>
      </div>

      {/* Header with breadcrumb */}
      <div className="flex items-start justify-between">
        <div className="space-y-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate(`/admin/materials/programs/${programId}`)}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to {session.programName}</span>
          </Button>
          
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900">
                Session {session.sessionNumber}: {session.name}
              </h2>
              <Badge variant="secondary" className="text-sm">
                {session.programCode}
              </Badge>
              <Badge variant="default" className="bg-green-100 text-green-800">
                {session.currentVersion}
              </Badge>
              {/* Inheritance Status Badge */}
              <Badge 
                variant="outline" 
                className="text-sm bg-blue-50 text-blue-700 border-blue-200 flex items-center space-x-1"
              >
                <GitBranch className="h-3 w-3" />
                <span>{materials.length > 0 ? 'Inherits from Program' : 'No Materials'}</span>
              </Badge>
            </div>
            <p className="text-gray-600 max-w-3xl">
              {session.description}
            </p>
            {/* Inheritance Info */}
            <div className="flex items-center space-x-4 mt-3 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
              <GitBranch className="h-4 w-4 text-blue-600" />
              <span>This session inherits materials from the Program level.</span>
              <Link 
                to={`/admin/materials/programs/${programId}/materials`}
                className="text-blue-600 hover:text-blue-800 underline font-medium"
              >
                Manage Program Materials →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Session Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2 text-gray-500 text-sm mb-1">
              <FileText className="h-4 w-4" />
              <span>Materials</span>
            </div>
            <div className="text-2xl font-bold">{materials.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2 text-gray-500 text-sm mb-1">
              <Users className="h-4 w-4" />
              <span>Active SOs</span>
            </div>
            <div className="text-2xl font-bold">{session.activeSOs}</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2 text-gray-500 text-sm mb-1">
              <Clock className="h-4 w-4" />
              <span>Duration</span>
            </div>
            <div className="text-2xl font-bold">{session.duration}m</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2 text-gray-500 text-sm mb-1">
              <Download className="h-4 w-4" />
              <span>Total Downloads</span>
            </div>
            <div className="text-2xl font-bold">
              {materials.reduce((sum, material) => sum + material.downloadCount, 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="current" className="flex items-center space-x-2">
            <FileText className="h-4 w-4" />
            <span>Current Materials</span>
          </TabsTrigger>
          <TabsTrigger value="upload" className="flex items-center space-x-2">
            <Upload className="h-4 w-4" />
            <span>Upload</span>
          </TabsTrigger>
          <TabsTrigger value="versions" className="flex items-center space-x-2">
            <History className="h-4 w-4" />
            <span>Version History</span>
          </TabsTrigger>
          <TabsTrigger value="impact" className="flex items-center space-x-2">
            <Eye className="h-4 w-4" />
            <span>Impact Preview</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="current" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Current Materials ({session.currentVersion})</CardTitle>
                  <CardDescription>
                    Materials currently active for this session
                  </CardDescription>
                </div>
                <Button size="sm" className="flex items-center space-x-2">
                  <Plus className="h-4 w-4" />
                  <span>Add Files</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {materials.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">No materials uploaded yet</p>
                  <Button className="mt-4" onClick={() => setActiveTab('upload')}>
                    Upload First Material
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {materials.map((material) => (
                    <div key={material.id} className="p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900 mb-1">
                            {material.displayName}
                          </h3>
                          <p className="text-sm text-gray-500 mb-2">
                            {material.filename} • {material.fileSize} • {material.downloadCount} downloads
                          </p>
                          <div className="flex space-x-4 text-xs text-gray-400">
                            <span>Uploaded {formatTimestamp(material.uploadedAt)}</span>
                            <span>by {material.uploadedBy}</span>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => material.cdnUrl && window.open(material.cdnUrl, '_blank')}
                            disabled={!material.cdnUrl}
                            className="flex items-center space-x-1"
                          >
                            <Download className="h-3 w-3" />
                            <span>Download</span>
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => material.shortLink && navigator.clipboard.writeText(material.shortLink)}
                            disabled={!material.shortLink}
                            className="flex items-center space-x-1"
                          >
                            <ExternalLink className="h-3 w-3" />
                            <span>Copy Link</span>
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeleteFile(material.id)}
                            className="flex items-center space-x-1 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      
                      {material.shortLink && (
                        <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded font-mono">
                          Short Link: <span className="text-blue-600">{material.shortLink}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="upload">
          <MaterialUpload 
            session={session}
            onUpload={handleFileUpload}
          />
        </TabsContent>

        <TabsContent value="versions">
          <VersionControl 
            session={session}
            versions={versions}
            onRollback={handleVersionRollback}
          />
        </TabsContent>

        <TabsContent value="impact">
          <ImpactPreview 
            session={session}
            impactData={impactData}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SessionMaterials;